import React, { useMemo } from "react";

import classnames from "classnames";
import { map } from "lodash";
import PropTypes from "prop-types";
import ReactSelect, { components } from "react-select";

import { Div } from "Common/HTML";
import type { SelectProps } from "Theme/Select";

type MultiSelectProps = SelectProps & {
  /** Optional placeholder text shown when nothing is selected */
  placeholder?: string;
  /** If provided, applies an error style when true (in addition to meta.touched+meta.error) */
  hasError?: boolean;
};

type OptionTuple = [string, string];
type OptionObject = { value: string; label: string };

function normalizeOptions(input: MultiSelectProps["options"]): OptionTuple[] {
  if (!input) return [];
  if (Array.isArray(input)) {
    if (input.length > 0 && typeof (input as any)[0] === "object" && !Array.isArray((input as any)[0])) {
      return (input as any[]).map((o) => [String(o.value), String(o.label)]);
    }
    return input as OptionTuple[];
  }
  return map(input as Record<string, string>, (value, label) => [String(value), String(label)]);
}

const CheckboxOption: React.FC<any> = (props) => {
  const { isSelected, label } = props;
  return (
    <components.Option {...props}>
      <input type="checkbox" checked={isSelected} onChange={() => null} style={{ marginRight: 8 }} />
      <span>{label}</span>
    </components.Option>
  );
};

const MenuListWithToggleCheckbox: React.FC<any> = (props) => {
  const { children } = props;

  return <components.MenuList {...props}>{children}</components.MenuList>;
};

const SIFormMultiSelect: React.FC<MultiSelectProps> = ({
  className,
  placeholder = "Select one or more",
  options = [],
  name,
  input,
  meta,
  showToggleAll = true,
  hasError,
  ...rest
}) => {
  const base = "SIFormMultiSelect";

  const normalized: OptionTuple[] = useMemo(() => normalizeOptions(options), [options]);
  const optionObjects: OptionObject[] = useMemo(() => normalized.map(([value, label]) => ({ value, label })), [normalized]);

  const selectedOptions: OptionObject[] = useMemo(() => {
    const v = input?.value;
    const asArray = Array.isArray(v) ? v : v ? [v] : [];
    const byValue = new Map(optionObjects.map((o) => [o.value, o]));
    return asArray.map((val: any) => String(val)).map((val) => byValue.get(val) || { value: val, label: val });
  }, [input?.value, optionObjects]);

  const classes = classnames(base, className, {
    [`${base}--error`]: Boolean(hasError || (meta && meta.touched && meta.error !== undefined)),
  });

  const propagate = (selected: OptionObject[] | null) => {
    const next = (selected ?? []).map((o) => o.value);
    input?.onChange?.(next);
    if (typeof (rest as any).onChange === "function") {
      (rest as any).onChange(next);
    }
  };

  return (
    <Div className={`w-7/12 ${base}__wrapper`}>
      <ReactSelect
        inputId={name}
        name={name}
        className={classes}
        classNamePrefix={base}
        options={optionObjects}
        value={selectedOptions}
        onChange={(newValue) => propagate(newValue as OptionObject[] | null)}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        placeholder={placeholder}
        onBlur={input?.onBlur}
        onFocus={input?.onFocus}
        components={{
          Option: CheckboxOption,
          MenuList: MenuListWithToggleCheckbox,
        }}
        noOptionsMessage={() => "No options"}
        {...rest}
        showToggleAll={showToggleAll}
      />
    </Div>
  );
};

SIFormMultiSelect.propTypes = {
  placeholder: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default SIFormMultiSelect;
